import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  DocumentTextIcon,
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserGroupIcon,
  BuildingIcon,
  PrinterIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const EUR_RATE = 1.95583; // Euro to BGN conversion rate

const InvoiceDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    method: t('invoiceDetails.bankTransfer'),
    reference: ''
  });
  const [downloadDropdown, setDownloadDropdown] = useState(false);
  const [printDropdown, setPrintDropdown] = useState(false);

  useEffect(() => {
    fetchInvoiceDetails();
  }, [id]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setDownloadDropdown(false);
        setPrintDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (message.type === 'success' && message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/finance/invoices/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(data);
      } else {
        throw new Error('Failed to fetch invoice details');
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
              setMessage({ type: 'error', text: t('invoiceDetails.failedToLoadInvoiceDetails') });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/finance/invoices/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('invoiceDetails.invoiceDeletedSuccessfully') });
        setTimeout(() => {
          navigate('/admin/dashboard/invoices?tab=list');
        }, 1500);
      } else {
        throw new Error(t('invoiceDetails.failedToDeleteInvoice'));
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setMessage({ type: 'error', text: t('invoiceDetails.failedToDeleteInvoice') });
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/finance/invoices/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('invoiceDetails.invoiceStatusUpdatedSuccessfully') });
        fetchInvoiceDetails();
      } else {
        throw new Error(t('invoiceDetails.failedToUpdateInvoiceStatus'));
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
      setMessage({ type: 'error', text: t('invoiceDetails.failedToUpdateInvoiceStatus') });
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      setMessage({ type: 'error', text: t('invoiceDetails.pleaseEnterValidPaymentAmount') });
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('jwtToken');
      
      const response = await fetch(`/api/finance/invoices/${id}/payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(paymentData.amount),
          method: paymentData.method,
          reference: paymentData.reference
        })
      });

      if (response.ok) {
        const updatedInvoice = await response.json();
        setInvoice(updatedInvoice);
        setMessage({ type: 'success', text: t('invoiceDetails.paymentRecordedSuccessfully') });
        setShowPaymentModal(false);
        setPaymentData({ amount: '', method: t('invoiceDetails.bankTransfer'), reference: '' });
      } else {
        throw new Error(t('invoiceDetails.failedToRecordPayment'));
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      setMessage({ type: 'error', text: t('invoiceDetails.failedToRecordPayment') });
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Sent':
        return 'bg-blue-100 text-blue-800';
      case 'Partial':
        return 'bg-yellow-100 text-yellow-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'Draft':
        return 'bg-gray-300 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Paid':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'Sent':
        return <ClockIcon className="h-5 w-5" />;
      case 'Partial':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      case 'Overdue':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      case 'Draft':
        return <DocumentTextIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  // Euro conversion rate (fixed rate for Bulgaria)
  const EUR_RATE = 1.95583;

  const formatCurrency = (amount, currency = 'BGN') => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const convertToEuro = (bgnAmount) => {
    return bgnAmount / EUR_RATE;
  };

  const formatEuro = (amount) => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Helper function to format description with line breaks at 55 characters
  const formatDescription = (description) => {
    if (!description) return '';
    
    const lines = description.split('\n');
    const processedLines = lines.map(line => {
      if (line.length <= 55) return line;
      
      // Break at 55 characters, try to break at word boundary
      const words = line.split(' ');
      let currentLine = '';
      let result = '';
      
      for (let word of words) {
        if ((currentLine + word).length <= 55) {
          currentLine += (currentLine ? ' ' : '') + word;
        } else {
          if (currentLine) {
            result += (result ? '\n' : '') + currentLine;
            currentLine = word;
          } else {
            // Word is longer than 55 chars, force break
            result += (result ? '\n' : '') + word.substring(0, 55);
            currentLine = word.substring(55);
          }
        }
      }
      
      if (currentLine) {
        result += (result ? '\n' : '') + currentLine;
      }
      
      return result;
    });
    
    return processedLines.join('\n');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('bg-BG');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('bg-BG');
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  const handleDownload = async (invoiceType = 'ORIGINAL') => {
    try {
      setSaving(true);
      const token = localStorage.getItem('jwtToken');
      
      const response = await fetch(`/api/finance/invoices/${id}/pdf?type=${invoiceType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoice.invoiceNumber}-${invoiceType}.pdf`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setMessage({ type: 'success', text: t('invoiceDetails.invoiceDownloadedSuccessfully', { type: invoiceType }) });
      } else {
        throw new Error(t('invoiceDetails.failedToDownloadInvoiceAsPDF'));
      }
    } catch (error) {
      console.error('Error downloading invoice as PDF:', error);
      setMessage({ type: 'error', text: t('invoiceDetails.failedToDownloadInvoiceAsPDF') });
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async (invoiceType = 'ORIGINAL') => {
    try {
      setSaving(true);
      const token = localStorage.getItem('jwtToken');
      
      const response = await fetch(`/api/finance/invoices/${id}/pdf?type=${invoiceType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Open PDF in new tab for preview and printing
        const newWindow = window.open(url, '_blank');
        
        // Clean up URL after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 10000);
        
      } else {
        throw new Error(t('invoiceDetails.failedToLoadInvoiceForPrinting'));
      }
    } catch (error) {
      console.error('Error printing invoice:', error);
      setMessage({ type: 'error', text: t('invoiceDetails.failedToPrintInvoice') });
    } finally {
      setSaving(false);
    }
  };

  const totalPaid = invoice?.paymentHistory?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
  const remainingAmount = invoice ? invoice.totalAmount - totalPaid : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t('invoiceDetails.loadingInvoiceDetails')}</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('invoiceDetails.invoiceNotFound')}</p>
          <button
            onClick={() => navigate('/admin/dashboard/invoices?tab=list')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            {t('invoiceDetails.backToInvoiceList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard/invoices?tab=list')}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{invoice.invoiceNumber}</h1>
                <p className="text-gray-600">{t('invoiceDetails.invoiceDetails')}</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <div className="relative">
                <button 
                  onClick={() => setDownloadDropdown(!downloadDropdown)}
                  disabled={saving}
                  className="text-green-600 hover:text-green-900 p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t('invoiceDetails.downloadAsPDF')}
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
                {downloadDropdown && (
                  <div 
                    className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handleDownload('ORIGINAL');
                          setDownloadDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('invoiceDetails.downloadOriginal')}
                      </button>
                      <button
                        onClick={() => {
                          handleDownload('COPY');
                          setDownloadDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('invoiceDetails.downloadCopy')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative">
                <button 
                  onClick={() => setPrintDropdown(!printDropdown)}
                  disabled={saving}
                  className="text-purple-600 hover:text-purple-900 p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t('invoiceDetails.printPDF')}
                >
                  <PrinterIcon className="h-5 w-5" />
                </button>
                {printDropdown && (
                  <div 
                    className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handlePrint('ORIGINAL');
                          setPrintDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('invoiceDetails.printOriginal')}
                      </button>
                      <button
                        onClick={() => {
                          handlePrint('COPY');
                          setPrintDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('invoiceDetails.printCopy')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button className="text-blue-600 hover:text-blue-900 p-2">
                <PencilIcon className="h-5 w-5" />
              </button>
              <button 
                onClick={() => setShowDeleteModal(true)}
                className="text-red-600 hover:text-red-900 p-2"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <span>{message.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Invoice Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('invoiceDetails.invoiceInformation')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('invoiceDetails.invoiceNumber')}</label>
                  <p className="text-lg font-semibold text-gray-900">{invoice.invoiceNumber}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('invoiceDetails.invoiceType')}</label>
                  <p className="text-gray-900">{t(`invoiceDetails.invoiceType${invoice.invoiceType || 'ORIGINAL'}`)}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('invoiceDetails.status')}</label>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(invoice.status)}
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                      {t(`invoiceDetails.status${invoice.status}`)}
                    </span>
                    {invoice.status === 'Sent' && isOverdue(invoice.dueDate) && (
                      <span className="text-xs text-red-600 font-medium">{t('invoiceDetails.overdue')}</span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('invoiceDetails.issueDate')}</label>
                  <p className="text-gray-900">{formatDate(invoice.issueDate)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('invoiceDetails.dueDate')}</label>
                  <p className={`${isOverdue(invoice.dueDate) ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                    {formatDate(invoice.dueDate)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('invoiceDetails.currency')}</label>
                  <p className="text-gray-900">{invoice.currency}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('invoiceDetails.project')}</label>
                  <p className="text-gray-900">
                    {invoice.projectId ? (
                      <span>{invoice.projectId.name}</span>
                    ) : (
                      <span className="text-gray-400">{t('invoiceDetails.noProjectAssigned')}</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('invoiceDetails.lineItems')}</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceDetails.description')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceDetails.quantity')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceDetails.unitPrice')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceDetails.total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoice.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          <div className="whitespace-pre-line">
                            {formatDescription(item.description)}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 text-right">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 text-right">
                          {formatCurrency(item.unitPrice, invoice.currency)}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 text-right">
                          <div>{formatCurrency(item.quantity * item.unitPrice, invoice.currency)}</div>
                          <div className="text-xs text-blue-600">{formatEuro(convertToEuro(item.quantity * item.unitPrice))}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="mt-6 border-t border-gray-200 pt-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600">{t('invoiceDetails.subtotal')}:</span>
                    <span className="font-medium">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600">{t('invoiceDetails.taxAmount')}:</span>
                    <span className="font-medium">{formatCurrency(invoice.taxAmount, invoice.currency)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2">
                    <div className="flex justify-between text-xl font-bold">
                      <span className="text-gray-900">{t('invoiceDetails.totalAmount')}:</span>
                      <span className="text-blue-600">{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Euro Conversion */}
                <div className="mt-4 border-t border-gray-200 pt-4 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('invoiceDetails.euroEquivalent')} (1 EUR = 1.95583 BGN)</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('invoiceDetails.subtotal')}:</span>
                      <span className="font-medium">{formatEuro(convertToEuro(invoice.subtotal))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('invoiceDetails.taxAmount')}:</span>
                      <span className="font-medium">{formatEuro(convertToEuro(invoice.taxAmount))}</span>
                    </div>
                    <div className="border-t border-gray-300 pt-2">
                      <div className="flex justify-between text-base font-bold">
                        <span className="text-gray-900">{t('invoiceDetails.totalAmount')}:</span>
                        <span className="text-green-600">{formatEuro(convertToEuro(invoice.totalAmount))}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('invoiceDetails.notes')}</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Company & Partner Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('invoiceDetails.companyAndPartner')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('invoiceDetails.fromCompany')}</label>
                  <div className="text-sm text-gray-900">
                    <p className="font-medium">{invoice.companyId?.name}</p>
                    <p>{invoice.companyId?.address}</p>
                    <p>{invoice.companyId?.city}, {invoice.companyId?.country}</p>
                    <p>{t('invoiceDetails.taxId')}: {invoice.companyId?.taxId}</p>
                {invoice.companyId?.vatTaxId && (
                  <p>{t('invoiceDetails.vatTaxId')}: BG{invoice.companyId.vatTaxId}</p>
                )}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('invoiceDetails.toPartner')}</label>
                  <div className="text-sm text-gray-900">
                    <p className="font-medium">{invoice.partnerId?.name}</p>
                    <p>{invoice.partnerId?.address}</p>
                    <p>{invoice.partnerId?.city}, {invoice.partnerId?.country}</p>
                    <p>{t('invoiceDetails.contact')}: {invoice.partnerId?.contactPerson}</p>
                    <p>{t('invoiceDetails.email')}: {invoice.partnerId?.email}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('invoiceDetails.paymentStatus')}</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('invoiceDetails.totalAmount')}:</span>
                  <span className="text-sm font-medium">{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('invoiceDetails.amountPaid')}:</span>
                  <span className="text-sm font-medium text-green-600">{formatCurrency(totalPaid, invoice.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('invoiceDetails.remaining')}:</span>
                  <span className={`text-sm font-medium ${remainingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(remainingAmount, invoice.currency)}
                  </span>
                </div>
              </div>
              
              {/* Euro Conversion for Payment Status */}
              <div className="mt-3 pt-3 border-t border-gray-200 bg-blue-50 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-gray-900 mb-2">{t('invoiceDetails.euroEquivalent')}</h4>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">{t('invoiceDetails.total')}:</span>
                    <span className="font-medium">{formatEuro(convertToEuro(invoice.totalAmount))}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">{t('invoiceDetails.paid')}:</span>
                    <span className="font-medium text-green-600">{formatEuro(convertToEuro(totalPaid))}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">{t('invoiceDetails.remaining')}:</span>
                    <span className={`font-medium ${remainingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatEuro(convertToEuro(remainingAmount))}
                    </span>
                  </div>
                </div>
              </div>

              {remainingAmount > 0 && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="w-full mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>{t('invoiceDetails.recordPayment')}</span>
                </button>
              )}
            </div>

            {/* Status Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('invoiceDetails.statusActions')}</h3>
              
              <div className="space-y-2">
                {invoice.status === 'Draft' && (
                  <button
                    onClick={() => updateStatus('Sent')}
                    disabled={saving}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    {t('invoiceDetails.markAsSent')}
                  </button>
                )}
                
                {invoice.status === 'Sent' && (
                  <button
                    onClick={() => updateStatus('Paid')}
                    disabled={saving}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    {t('invoiceDetails.markAsPaid')}
                  </button>
                )}
                
                {invoice.status !== 'Draft' && (
                  <button
                    onClick={() => updateStatus('Draft')}
                    disabled={saving}
                    className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    {t('invoiceDetails.markAsDraft')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Payment History */}
        {invoice.paymentHistory && invoice.paymentHistory.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('invoiceDetails.paymentHistory')}</h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoiceDetails.date')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoiceDetails.amount')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoiceDetails.method')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoiceDetails.reference')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoice.paymentHistory.map((payment, index) => (
                    <tr key={index}>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {formatDateTime(payment.date)}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-green-600">
                        {formatCurrency(payment.amount, invoice.currency)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {payment.method}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {payment.reference || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">{t('invoiceDetails.deleteInvoice')}</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  {t('invoiceDetails.deleteInvoiceConfirmation')} <strong>{invoice.invoiceNumber}</strong>? 
                  {t('invoiceDetails.deleteInvoiceWarning')}
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  {t('invoiceDetails.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                >
                  {t('invoiceDetails.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('invoiceDetails.recordPayment')}</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceDetails.amount')} *
                </label>
                <input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                  min="0"
                  max={remainingAmount}
                  step="0.01"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={`${t('invoiceDetails.max')}: ${formatCurrency(remainingAmount, invoice.currency)}`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceDetails.paymentMethod')}
                </label>
                <select
                  value={paymentData.method}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, method: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Bank Transfer">{t('invoiceDetails.bankTransfer')}</option>
                  <option value="Cash">{t('invoiceDetails.cash')}</option>
                  <option value="Card">{t('invoiceDetails.card')}</option>
                  <option value="Check">{t('invoiceDetails.check')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceDetails.referenceOptional')}
                </label>
                <input
                  type="text"
                  value={paymentData.reference}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, reference: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('invoiceDetails.transactionReference')}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  {t('invoiceDetails.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? t('invoiceDetails.recording') : t('invoiceDetails.recordPayment')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetails; 